import { Typography, typographyVariants } from "../../typography";
import { format } from "date-fns";
import { AssistantAvatarWithBg } from "../../assistant-avatar";
import { UserConversationRoleEnum } from "@aichat/types/enums/index";
import { LiveAgent } from "@aichat/types/socket/widget.types";
import { getHSLAString, HSLColor } from "@aichat/aichat-lib";
import { cn } from "@aichat/ui/lib/utils";
import { Avatar, AvatarFallback } from "../../avatar";
import { PropsWithChildren } from "react";
import { darkHSLA, lightHSLA } from "@aichat/constants/widget";

export type MessageContainerProps = PropsWithChildren<{
  role: UserConversationRoleEnum;
  createdAt: string;
  liveChatAgent?: LiveAgent;
  isFirstSameRoleBubble?: boolean;
  isLastSameRoleBubble?: boolean;
  isMiddleSameRoleBubble?: boolean;
  conversationLabelName?: string;
  shouldAnimate?: boolean;
  type?: "portal-chat" | "widget-chat";
  hideTime?: boolean;
  hideAvatar?: boolean;
  widgetConfig: {
    avatarUrl?: string;
    avatarType: "light" | "dark";
    liveAgentAvatarUrl?: string;
    liveAgentAvatarType?: "light" | "dark";
    chatbotDisplayName?: string;
    brandColor: HSLColor;
    textColor: "light" | "dark";
    isThemeSleek: boolean;
    productCarouselButtonLabel?: string;
  };
}>;

const roleToNameMap = {
  bot: "Bot",
  live_chat_agent: "Agent",
  customer: "Customer",
  system: "System",
  pending: "Pending",
} satisfies Record<UserConversationRoleEnum, string>;

function MessageContainer(props: MessageContainerProps) {
  if (props.type === "portal-chat")
    return <PortalMessageContainer {...props} />;
  return <WidgetMessageContainer {...props} />;
}

function WidgetMessageContainer({
  role,
  children,
  createdAt,
  liveChatAgent,
  isFirstSameRoleBubble,
  isLastSameRoleBubble,
  isMiddleSameRoleBubble,
  hideAvatar,
  hideTime,
  widgetConfig,
}: MessageContainerProps) {
  const {
    avatarType,
    chatbotDisplayName,
    avatarUrl,
    brandColor,
    textColor,
    isThemeSleek,
    liveAgentAvatarType,
    liveAgentAvatarUrl,
  } = widgetConfig;

  const brandColorHSLA = getHSLAString(brandColor);
  const brandColorHSLADarkLight = getHSLAString({ ...brandColor, a: 0.7 });
  const brandColorHSLALight = getHSLAString({ ...brandColor, a: 0.5 });
  const brandColorHSLALowLight = getHSLAString({ ...brandColor, a: 0.2 });
  const brandColorHSLAVeryLight = getHSLAString({ ...brandColor, a: 0.1 });

  const timeColorHSLA = getHSLAString({
    ...(textColor === "dark" ? darkHSLA : lightHSLA),
    a: 0.8,
  });

  const avatarComponent = (
    <div className="flex items-center gap-2">
      {!hideAvatar && (
        <AssistantAvatarWithBg
          containerProps={{
            className: "size-6 rounded-lg",
            style: {
              background: `linear-gradient(to bottom right, ${brandColorHSLALight},${brandColorHSLA})`,
            },
          }}
          avatarType={liveChatAgent ? "live-agent" : "bot"}
          avatarProps={{
            avatarType: liveChatAgent ? "live-agent" : "bot",
            customUrl: liveChatAgent ? liveAgentAvatarUrl : avatarUrl,
            className: "size-4",
            variant: liveChatAgent
              ? (liveAgentAvatarType ?? "light")
              : avatarType,
          }}
        />
      )}
      <Typography variant="subtleMedium">
        {liveChatAgent ? liveChatAgent.name : chatbotDisplayName}
      </Typography>
      <div className="size-1 bg-foreground rounded-full" />
      <Typography variant="subtleMedium">{roleToNameMap[role]}</Typography>
    </div>
  );

  const formattedTime = createdAt
    ? format(new Date(createdAt), "HH:mm aa")
    : "";

  if (
    role === UserConversationRoleEnum.BOT ||
    role === UserConversationRoleEnum.LIVE_CHAT_AGENT
  )
    return (
      <div
        className={
          isThemeSleek
            ? "rounded-bl-2xl rounded-e-2xl bg-gradient-to-r from-white/65 from-0% to-white/85 to-100% w-fit backdrop-blur-2xl firefox:backdrop-blur-none firefox:bg-white/75"
            : ""
        }
      >
        <div
          className={cn(
            "p-4 flex flex-col gap-3 w-fit max-w-[374px] rounded-xl bg-background border border-border shadow-[0px_1px_2px_0px_#0000000D]",
            isFirstSameRoleBubble ? "rounded-bl-[4px]" : "",
            isMiddleSameRoleBubble ? "rounded-l-[4px]" : "",
            isLastSameRoleBubble ? "rounded-tl-[4px]" : "",
            isThemeSleek
              ? "rounded-tl-none rounded-bl-2xl rounded-e-2xl shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)] backdrop-blur-[43px] firefox:backdrop-blur-none"
              : "rounded-xl",
          )}
          style={
            isThemeSleek
              ? {
                  background: `linear-gradient(to right, ${brandColorHSLAVeryLight}, ${brandColorHSLALowLight})`,
                }
              : undefined
          }
        >
          <div className="flex flex-col gap-2">
            {isFirstSameRoleBubble && avatarComponent}
            {children}
          </div>
          {!hideTime && (
            <Typography
              className="text-muted-foreground lowercase"
              variant="tiny"
            >
              {formattedTime}
            </Typography>
          )}
        </div>
      </div>
    );
  return (
    <div
      className={cn(
        "ml-auto w-fit max-w-[374px] text-right p-4 flex flex-col gap-2 shadow-[2px_2px_2px_0px_#0000000D]",
        "[&>.markdown.prose]:text-[var(--text-theme)]",
        isThemeSleek
          ? "rounded-br-2xl rounded-s-2xl backdrop-blur-2xl firefox:backdrop-blur-none shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)]"
          : "rounded-xl",
      )}
      style={{
        background: isThemeSleek
          ? `linear-gradient(to right, ${brandColorHSLADarkLight}, ${brandColorHSLA})`
          : brandColorHSLA,
        ...({
          "--text-theme": textColor === "light" ? "white" : "black",
        } as React.CSSProperties),
      }}
    >
      {children}
      {!hideTime && (
        <Typography
          variant="tiny"
          className="ml-auto lowercase text-inherit"
          style={{
            color: timeColorHSLA,
          }}
        >
          {formattedTime}
        </Typography>
      )}
    </div>
  );
}

function PortalMessageContainer(props: MessageContainerProps) {
  const { role } = props;

  if (role === UserConversationRoleEnum.CUSTOMER)
    return <PortalCustomerMessageContainer {...props} />;
  return <PortalBotAgentMessageContainer {...props} />;
}

function PortalCustomerMessageContainer({
  isLastSameRoleBubble,
  conversationLabelName,
  isFirstSameRoleBubble,
  isMiddleSameRoleBubble,
  children,
  createdAt,
  hideTime,
}: MessageContainerProps) {
  const formattedTime = format(new Date(createdAt), "HH:mm aa");
  return (
    <div className="flex justify-start items-end gap-2">
      {isLastSameRoleBubble ? (
        <Avatar className="size-6 grid place-items-center bg-background-100 border border-border">
          <AvatarFallback
            className={typographyVariants({
              variant: "pBold",
              className:
                "rounded-xl text-muted-foreground grid place-items-center",
            })}
          >
            {conversationLabelName?.at(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="size-6" />
      )}
      <div
        className={cn(
          "max-w-[75%] min-w-[20%] rounded-xl bg-background-100 border border-border border-opacity-30bg-pbg- w-fit p-3 flex flex-col gap-4",
          isFirstSameRoleBubble ? "rounded-bl-[4px]" : "",
          isMiddleSameRoleBubble ? "rounded-l-[4px]" : "",
          isLastSameRoleBubble ? "rounded-tl-[4px]" : "",
        )}
      >
        {children}
        {!hideTime && (
          <Typography variant="tiny" className="lowercase text-inherit">
            {formattedTime}
          </Typography>
        )}
      </div>
    </div>
  );
}

function PortalBotAgentMessageContainer({
  children,
  createdAt,
  role,
  isFirstSameRoleBubble,
  isMiddleSameRoleBubble,
  isLastSameRoleBubble,
  liveChatAgent,
  hideTime,
  widgetConfig,
}: MessageContainerProps) {
  const formattedTime = format(new Date(createdAt), "HH:mm aa");
  const { avatarType, avatarUrl, liveAgentAvatarUrl, liveAgentAvatarType } =
    widgetConfig;

  return (
    <div className="flex justify-end rounded-xl gap-2 items-end">
      <div
        className={cn(
          "max-w-[75%] min-w-[20%] rounded-xl flex flex-col gap-4 w-fit p-4",
          role === UserConversationRoleEnum.BOT
            ? "bg-primary-50"
            : "bg-blue-50",
          isFirstSameRoleBubble ? "rounded-br-[4px]" : "",
          isMiddleSameRoleBubble ? "rounded-r-[4px]" : "",
          isLastSameRoleBubble ? "rounded-tr-[4px]" : "",
        )}
      >
        {children}
        {!hideTime && (
          <Typography variant="tiny" className="ml-auto lowercase text-inherit">
            {formattedTime}
          </Typography>
        )}
      </div>
      <div className="shrink-0">
        {isLastSameRoleBubble ? (
          <AssistantAvatarWithBg
            containerProps={{
              className: "size-6 rounded-lg",
            }}
            avatarType={liveChatAgent ? "live-agent" : "bot"}
            avatarProps={{
              avatarType: liveChatAgent ? "live-agent" : "bot",
              customUrl: liveChatAgent ? liveAgentAvatarUrl : avatarUrl,
              className: "size-4",
              variant: liveChatAgent
                ? (liveAgentAvatarType ?? "light")
                : (avatarType ?? "light"),
            }}
          />
        ) : (
          <div className="size-6" />
        )}
      </div>
    </div>
  );
}

export default MessageContainer;
